import React, {
  useCallback,
  useState,
} from 'react';
import { observer } from 'mobx-react';
import './StatusBar.css';
import MixBar from '../MixBar/MixBar';
import Timer from '../Timer/Timer';
import { withTranslation } from 'react-i18next';
import { displayDate } from '../../utils/date';
import { useStores } from '../../hooks/use-stores';
import { RunMode } from '../../constants/Configuration';

const StatusBar = observer((props) => {
  const { t } = props;
  const { batchStore, scheduleStore, settingsStore, newAuthStore, connectionStore } = useStores();
  const { running } = batchStore;
  const dataList = [ ...running ].reverse();
  const [, updateState] = useState();
  const forceUpdate = useCallback(() => updateState({}), []);

  const renderStatus = (data) => {
    const { isRunning, isDemo, queue, queueSize, replayMode } = batchStore;
    const { configuration } = settingsStore;
    const cn = isRunning ? 'active' : '';
    const d = data || {
      order: {},
      ticket: {},
      mix: {},
      material: {},
    };
    const order = d.order || {};

    return (
      <div key={data._id || data.id} className={`batch-status-bar ${cn} is-hold-load-${data.isHoldLoad}`} data-e2e-id="batch-status-bar">
        {configuration && !!configuration.batchClock &&
        <div className="status-time"><Timer startTime={data.loadStartTime} stop={data.loadEndTime} /></div>
        }
        <div className="hidden-xs">
          <div className="status-text" data-e2e-id="batch-status-bar-status">{t('Status')}: <span>{!!isDemo || !!d.isDemo ? t('Demo') : 'LOAD' }</span></div>
          <div className="status-queue" data-e2e-id="batch-status-bar-queue">{t('Queue')}: <span>{queueSize} ({queue.length})</span></div>
        </div>
        <div>
          <div className="status-job" data-e2e-id="batch-status-bar-order-code">{t('Code')}: <span>{d.orderCode}</span></div>
          <div className="status-ticket" data-e2e-id="batch-status-bar-ticket">{t('Ticket')}: <span>{d.tId}</span></div>
        </div>
        <div className="hidden-xs">
          <div className="status-customer" title={data.customerName} data-e2e-id="batch-status-bar-customer">{t('Customer')}: <span>{order.customerCode}</span></div>
          {d.mix?.unitSymbol &&
          <div className="status-unit" data-e2e-id="batch-status-bar-unit">{t('Unit')}: <span>{d.mix.unitSymbol}</span></div>
          }
        </div>
        <div>
          {d.mix?.itemCode &&
          <div className="status-mix" data-e2e-id="batch-status-bar-mix">{t('Mix')}: <span>{d.mix.itemCode}</span></div>
          }
          {d.material?.code &&
          <div className="status-mix" data-e2e-id="batch-status-bar-material">{t('Material')}: <span>{d.material.code}</span></div>
          }
          <div className="status-truck" data-e2e-id="batch-status-bar-truck">{t('Truck')}: <span>{d.truckCode}</span></div>
        </div>
        <div className="hidden-xs">
          <div className="status-size" data-e2e-id="batch-status-bar-size">{t('Size')}: <span>{d.batchSize}</span></div>
          <div className="status-dropsize" data-e2e-id="batch-status-bar-dropsize">{t('Drop Size')}: <span>{d.dropSize}</span></div>
        </div>
        <div>
          <div className="status-balance" data-e2e-id="batch-status-bar-balance">{t('Balance')}: <span>{d.balance}</span></div>
          <div className="status-drop-all" data-e2e-id="batch-status-bar-drop-all">{t('Drop')}: <span>{d.currentDrop}</span> of <span>{d.allDrops}</span></div>
        </div>
        <div>
          <div className="status-discharge-sequence" title={d.dischargeSequenceName} data-e2e-id="batch-status-bar-discharge-sequence">{t('Disch Seq')}: <span>{d.dischargeSequenceId ? d.dischargeSequenceName || d.dischargeSequenceId : 'DEFAULT'}</span></div>
          {(!!d.runMode || d.runMode !== RunMode.Normal) &&
          <div className="status-mode" data-e2e-id="batch-status-bar-mode">{t('Mode')}: <span>{d.runMode} {d.isTestMode ? 'TEST' : ''}</span></div>
          }
        </div>
        {!!d.mixerId && (
          <div className="hidden-xs">
            <div className="status-mixer" data-e2e-id="batch-status-bar-mixer">{t('Mixer')}: <span>{d.mixerId}</span></div>
          </div>
        )}
        {d.mix?.itemCode && (
          <div className="hidden-xs status-mix-wrapper">
            <MixBar data={d}/>
          </div>
        )}

        <button
          className="btn btn-sm btn-default btn-hold-load"
          data-e2e-id="btn-batch-status-bar-hold-resume-load"
          disabled={replayMode}
          onClick={() => {
            if (data.isHoldLoad) {
              batchStore.resumeLoad(data.id, data.currentDrop);
            } else {
              batchStore.holdLoad(data.id, data.currentDrop);
            }
            forceUpdate();
          }}>
          {data.isHoldLoad ? <i className="fa fa-play" /> : <i className="fa fa-pause" />}
        </button>

        {data.isHoldLoad && (
          <button
            className="btn btn-sm btn-default btn-remove-load"
            data-e2e-id="btn-batch-status-bar-remove-load"
            disabled={replayMode}
            onClick={() => {
              batchStore.removeLoad(data.id, data.currentDrop);
              forceUpdate();
            }}>
            <i className="fa fa-close"/>
          </button>
        )}

        {data.remoteUser && (
          <div className="hidden-xs status-user" data-e2e-id="batch-status-bar-remote-user">
            <div>{t('Started by remote user')}:
              <div>{data.remoteUser?.login}</div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderLastLoad = () => {
    const { configuration } = settingsStore;
    const { timezone } = scheduleStore;
    const { lastLoad, running } = batchStore;
    const data = lastLoad;
    const { user } = newAuthStore;

    return configuration?.batchInfoPermanent && data && !running.length ? (
      <div className={'batch-status-bar last-load'}>
        <div className="status-last-load">{t('Last Load')}: <span>{displayDate(data.loadStartTime, timezone, 'LT', user.language)}</span></div>
        <div className="status-ticket">{t('Ticket')}: <span>{data.tId}</span></div>
        <div className="status-truck">{t('Truck')}: <span>{data.truckCode}</span></div>
        <div className="status-qty">{t('Qty')}: <span>{data.orderMixQuantity}</span></div>
        <div className="status-customer hidden-xs">{t('Customer')}: <span>{data.customerName}</span></div>
      </div>
    ) : '';
  };

  const getNextData = (index = 0) => {
    const { schedules } = scheduleStore;

    return schedules.length && schedules.length > index
      ? scheduleStore.isEmptyMixDesign(schedules[index]) ? getNextData(index + 1) : schedules[index]
      : '';
  };

  const renderNextLoad = () => {
    const { timezone } = scheduleStore;
    const data = getNextData();
    const { user } = newAuthStore;

    return data ? (
      <div className={'batch-status-bar next-load'}>
        <div className="status-load">{t('Next Load')}: <span>{displayDate(data.date, timezone, 'LT', user.language)}</span></div>
        <div className="status-ticket">{t('Ticket')}: <span>{data.dispatchTicketCode}</span></div>
        <div className="status-truck">{t('Truck')}: <span>{data.truckCode}</span></div>
        <div className="status-qty">{t('Qty')}: <span>{data.orderMixQuantity}</span></div>
        <div className="status-customer hidden-xs">{t('Customer')}: <span>{data.customerName}</span></div>
      </div>
    ) : '';
  };

  const tokenMissingAndIsOnline = !newAuthStore.authToken && connectionStore.isOnline;

  const renderError = () => {
    return (newAuthStore.tokenExpired || tokenMissingAndIsOnline) && (
      <div className="batch-status-bar status-bar-error">
        {t('Your token has expired! Please login again!')}
      </div>
    );
  };

  return (
    <div className="status-bar-wrapper">
      {renderError()}
      {renderNextLoad()}
      {renderLastLoad()}
      {dataList.map((data) => renderStatus(data))}
    </div>
  );

});

export default withTranslation()(StatusBar);

